import React, { useEffect, useMemo, useState } from 'react';

import cn from 'classnames/bind';
import styles from './styles.styl';
import { Sidebar } from '@consta/uikit/Sidebar';
import { Text } from '@consta/uikit/Text';
import { Button } from '@consta/uikit/Button';
import { DatePicker } from '@consta/uikit/DatePickerCanary';
import { IconCalendar } from '@consta/uikit/IconCalendar';
import { IconClose } from '@consta/uikit/IconClose';
import { Combobox } from '@consta/uikit/Combobox';
import { useShallowEqualSelector } from 'src/customHooks/useShallowEqualSelector';
import { userInitSelector } from 'src/redux/features/userInit/selectors';
import { journalMethodsNamesEnum } from 'src/utils/enum';
import { TextField } from '@consta/uikit/TextField';

const cx = cn.bind(styles);

export interface IJournalCurrentFilters {
  fist_at: string | null;
  last_at: string | null;
  ip_address: string | null;
  role_name: number | null;
  login: string | null;
  methods: string[] | null;
}

type SetFilterType = React.Dispatch<
  React.SetStateAction<Partial<IJournalCurrentFilters>>
>;

export interface IJournalFilterProps {
  isOpen: boolean;
  close: () => void;
  filter: Partial<IJournalCurrentFilters>;
  setFilter: SetFilterType;
  clearFilter: () => void;
}

const JournalFilter: React.FC<IJournalFilterProps> = ({
  isOpen,
  close,
  filter,
  setFilter,
  clearFilter,
}) => {
  const [userInit] = useShallowEqualSelector((state) => [
    userInitSelector(state),
  ]);

  const [localFilter, setLocalFilter] = useState<
    Partial<IJournalCurrentFilters>
  >({});

  useEffect(() => {
    isOpen && setLocalFilter(filter);
  }, [filter, isOpen]);

  const closeLocalFilter = () => {
    setLocalFilter({});
    close();
  };

  const filtersAreNotEmpty: boolean = useMemo(
    () =>
      Object.keys(localFilter).some(
        (key: keyof IJournalCurrentFilters) => !!localFilter[key],
      ),
    [localFilter],
  );

  const changeFilterValueByKey = (
    key: string,
    value: string | number | boolean | null | string[],
  ) => {
    setLocalFilter((prev: Partial<IJournalCurrentFilters>) => ({
      ...prev,
      [key]: value,
    }));
  };

  const applyLocalFilter = () => {
    setFilter(localFilter);
    close();
  };

  const journalMethodsFilter = [
    {
      id: 'Открытие модуля Модели',
      label: 'Открытие модуля Модели',
    },
    {
      id: 'Открытие модуля Модели (Детальная информация)',
      label: 'Открытие модуля Модели (Детальная информация)',
    },
    {
      id: 'Редактирование в модуле Модели',
      label: 'Редактирование в модуле Модели',
    },
    {
      id: 'Загрузка файла',
      label: 'Загрузка файла',
    },
    {
      id: 'Открытие модуля Пользователи',
      label: 'Открытие модуля Пользователи',
    },
    {
      id: 'Удаление в модуле Файлы',
      label: 'Удаление в модуле Файлы',
    },
  ];

  return (
    <Sidebar
      isOpen={isOpen}
      onClickOutside={close}
      hasOverlay={false}
      className={cx('JournalFilter')}
    >
      <Sidebar.Content className={cx('JournalFilter__content')}>
        <div className={cx('JournalFilter__header')}>
          <Text weight="bold">Фильтры</Text>
          {filtersAreNotEmpty && (
            <Button
              size="xs"
              label="Сбросить все фильтры"
              view="ghost"
              onClick={clearFilter}
            />
          )}
          <IconClose
            size="s"
            className={cx('JournalFilter__iconClose')}
            onClick={closeLocalFilter}
          />
        </div>
        {userInit && (
          <>
            <div className={cx('JournalFilter__antibodiesBlock')}>
              <div className={cx('JournalFilter__item')}>
                <Text view="secondary">Дата события от</Text>
                <DatePicker
                  type="date"
                  value={
                    localFilter.fist_at ? new Date(localFilter.fist_at) : null
                  }
                  onChange={({ value }) =>
                    changeFilterValueByKey(
                      'fist_at',
                      value ? value.toISOString() : null,
                    )
                  }
                  renderAdditionalControls={
                    <Button
                      size="s"
                      label="Очистить"
                      view="ghost"
                      className={cx('JournalFilter__dateEventResetButton')}
                      onClick={() => changeFilterValueByKey('fist_at', null)}
                    />
                  }
                  size="s"
                  rightSide={IconCalendar}
                />
              </div>
              <div className={cx('JournalFilter__item')}>
                <Text view="secondary">Дата события до</Text>
                <DatePicker
                  type="date"
                  value={
                    localFilter.last_at ? new Date(localFilter.last_at) : null
                  }
                  onChange={({ value }) =>
                    changeFilterValueByKey(
                      'last_at',
                      value ? value.toISOString() : null,
                    )
                  }
                  renderAdditionalControls={
                    <Button
                      size="s"
                      label="Очистить"
                      view="ghost"
                      className={cx('JournalFilter__dateEventResetButton')}
                      onClick={() => changeFilterValueByKey('last_at', null)}
                    />
                  }
                  size="s"
                  rightSide={IconCalendar}
                />
              </div>
            </div>
            <div className={cx('JournalFilter__item')}>
              <Text view="secondary">IP-адрес</Text>
              <TextField
                value={localFilter.ip_address ?? ''}
                onChange={({ value }) =>
                  changeFilterValueByKey('ip_address', value)
                }
                size="s"
                placeholder="Ввведите ip-адрес"
              />
            </div>
            <div className={cx('JournalFilter__item')}>
              <Text view="secondary">Роль</Text>
              <Combobox
                size="s"
                value={(() => {
                  if (localFilter.role_name) {
                    const item = userInit.roles.find(
                      (item) => item.key === String(localFilter.role_name),
                    );

                    if (!item) return null;
                    return { id: item.key, label: item.name };
                  }

                  return null;
                })()}
                items={userInit.roles.map(({ key, name }) => ({
                  id: key,
                  label: name,
                }))}
                onChange={({ value }) => {
                  changeFilterValueByKey(
                    'role_name',
                    value ? String(value.id) : null,
                  );
                }}
                placeholder="Выберите роль"
              />
            </div>

            <div className={cx('JournalFilter__item')}>
              <Text view="secondary">Действия</Text>
              <Combobox
                size="s"
                multiple
                value={(() => {
                  if (localFilter.methods?.length) {
                    const items = localFilter.methods
                      .filter((method) =>
                        journalMethodsFilter.find((item) => item.id === method),
                      )
                      .map((method) => ({
                        id: method,
                        label: journalMethodsFilter.find(
                          (item) => item.id === method,
                        )?.label,
                      }));
                    return items;
                  }

                  return null;
                })()}
                items={journalMethodsFilter}
                onChange={({ value }) => {
                  changeFilterValueByKey(
                    'methods',
                    value ? value.map((val) => val.id) : null,
                  );
                }}
                placeholder="Выберите действия"
              />
            </div>
          </>
        )}
      </Sidebar.Content>
      <Sidebar.Actions className={cx('JournalFilter__sidebarActions')}>
        <Button
          size="s"
          label="Отмена"
          view="secondary"
          onClick={closeLocalFilter}
        />
        <Button size="s" label="Применить" onClick={applyLocalFilter} />
      </Sidebar.Actions>
    </Sidebar>
  );
};

export default JournalFilter;
